import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBrandManifest,
  getBrandProducts,
} from "../../services/catalogService";
import { trackEvent } from "../../services/analyticsService";
import SafeImage from "../common/SafeImage";
import "../../styles/BrandPage.css";

/** UI page size for Previous/Next. Catalog brand shards remain 1000 products/page. */
const UI_PAGE_SIZE = 20;

function displayTitle(productTitle) {
  return productTitle && String(productTitle).trim()
    ? productTitle
    : "Untitled product";
}

const BrandPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [catalogPageData, setCatalogPageData] = useState(null);
  const [uiPage, setUiPage] = useState(1);
  const [groupedproductTitles, setGroupedproductTitles] = useState([]);
  const [selectedproductTitle, setSelectedproductTitle] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const trackedBrandRef = useRef(null);

  useEffect(() => {
    setUiPage(1);
    trackedBrandRef.current = null;
    setSelectedproductTitle(null);
  }, [brandId]);

  useEffect(() => {
    let cancelled = false;

    const loadBrandPage = async () => {
      if (!brandId) {
        setError("No brand provided.");
        setCatalogPageData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const brand = await getBrandManifest(brandId);
        if (cancelled) return;

        if (!brand) {
          setCatalogPageData(null);
          setGroupedproductTitles([]);
          setFilteredProducts([]);
          setError("Brand not found.");
          setLoading(false);
          return;
        }

        const catalogPageSize = Number(brand.pageSize) || 1000;
        const productCount = Number(brand.productCount) || 0;
        const totalUiPages = Math.max(
          1,
          Math.ceil(productCount / UI_PAGE_SIZE),
        );
        const safeUiPage = Math.min(Math.max(uiPage, 1), totalUiPages);
        const neededCatalogPage =
          productCount === 0
            ? 1
            : Math.floor(((safeUiPage - 1) * UI_PAGE_SIZE) / catalogPageSize) +
              1;

        const result = await getBrandProducts(brandId, neededCatalogPage);
        if (cancelled) return;

        if (!result) {
          setCatalogPageData(null);
          setGroupedproductTitles([]);
          setFilteredProducts([]);
          setError("Brand not found.");
          setLoading(false);
          return;
        }

        setCatalogPageData(result);

        const shardProducts = result.products || [];
        // Group only the currently loaded catalogue shard — never the full brand.
        const titles = [
          ...new Set(
            shardProducts.map((product) => displayTitle(product.productTitle)),
          ),
        ];
        setGroupedproductTitles(titles);

        const nextSelected =
          selectedproductTitle && titles.includes(selectedproductTitle)
            ? selectedproductTitle
            : titles[0] || null;
        setSelectedproductTitle(nextSelected);

        if (nextSelected) {
          setFilteredProducts(
            shardProducts.filter(
              (product) => displayTitle(product.productTitle) === nextSelected,
            ),
          );
        } else {
          setFilteredProducts([]);
        }

        if (
          result.productCount > 0 &&
          trackedBrandRef.current !== result.brand?.name
        ) {
          trackedBrandRef.current = result.brand.name;
          trackEvent("view_brand", {
            brand_name: result.brand.name,
          });
        }

        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading brand products:", err);
        setError("Failed to load products. Please try again later.");
        setCatalogPageData(null);
        setGroupedproductTitles([]);
        setFilteredProducts([]);
        setLoading(false);
      }
    };

    loadBrandPage();

    return () => {
      cancelled = true;
    };
    // selectedproductTitle is handled locally on click to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, uiPage]);

  const handleproductTitleClick = (productTitle) => {
    setSelectedproductTitle(productTitle);
    const shardProducts = catalogPageData?.products || [];
    setFilteredProducts(
      shardProducts.filter(
        (product) => displayTitle(product.productTitle) === productTitle,
      ),
    );
  };

  const handleProductClick = (product) => {
    if (!product?.productNumber) return;
    navigate(`/products/${product.productNumber}`, { state: product });
  };

  if (loading) return <p>Loading products...</p>;
  if (error) return <p>Error: {error}</p>;

  const productCount = catalogPageData?.productCount || 0;
  const totalUiPages = Math.max(1, Math.ceil(productCount / UI_PAGE_SIZE));
  const safeUiPage = Math.min(Math.max(uiPage, 1), totalUiPages);

  if (!productCount) {
    return <p>No products found for this brand.</p>;
  }

  const handleNext = () => {
    if (safeUiPage < totalUiPages) setUiPage(safeUiPage + 1);
  };

  const handlePrevious = () => {
    if (safeUiPage > 1) setUiPage(safeUiPage - 1);
  };

  return (
    <section className="brand-page">
      <div className="brand-content">
        {/* Left Sidebar */}
        <aside className="product-names-sidebar">
          <h3>Product Names</h3>
          <ul>
            {groupedproductTitles.map((name, index) => (
              <li
                key={`${name}-${index}`}
                className={`product-name-item ${
                  selectedproductTitle === name ? "selected" : ""
                }`}
                onClick={() => handleproductTitleClick(name)}
              >
                {name}
              </li>
            ))}
          </ul>
        </aside>

        {/* Right Product List */}
        <div className="products-list-container">
          <ul className="products-list">
            {filteredProducts.map((product, index) => (
              <li
                key={product.id || index}
                className="product-list-item"
                onClick={() => handleProductClick(product)}
              >
                <SafeImage
                  src={product.mediumImagePath}
                  alt={displayTitle(product.productTitle)}
                  className="list-image"
                />
                <div className="product-info">
                  <h3>{displayTitle(product.productTitle)}</h3>
                  <p>Part Number: {product.productNumber}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="pagination-controls">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={safeUiPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span>
              Page {safeUiPage} of {totalUiPages}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={safeUiPage === totalUiPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandPage;
